<template>
    <v-card class="overflow-hidden">
        <v-navigation-drawer
            v-model="showErwSuche"
            absolute
            bottom
            temporary
            right
            width="530px"
            color="kp-light-panel-bg"
        >
            <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                <v-spacer />
                <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
            </v-toolbar>
            <EventEnhancedSearch
                :initial-params="extendedSearchParams"
                @search="onEnhancedSearch"
                @reset="resetEnhancedSearch"
            />
        </v-navigation-drawer>
        <BaseRemoteList
            ref="eventList"
            v-model="selectedItems"
            class="event-list"
            :session-key="sessionKey"
            :subtitle="$t('deine_veranstaltungen')"
            :collection-class="EventCollection"
            :headers="headers"
            :filters="filters"
            :extra-params="extraParams"
            :options="{ sortBy: ['created'], sortDesc: [true] }"
            :item-class="() => 'event-list-row'"
            cache-data
            show-select
            :show-select-all="false"
            :new-btn="false"
            :height="height"
            @loading="loading = $event"
            @edit-item="onEdit"
            @dblclick:row="onRowDblClick"
            @item-selected="onItemSelected"
            @loaded="onLoaded"
        >
            <template v-if="['list', 'dashboard'].includes(displayContext)" #toolbar-start>
                <v-btn color="primary white--text" depressed :to="{ name: 'event-edit', params: { id: 'new' } }">
                    <KpIcon icon-cls="new" color="white" disabled-color="grey" left />
                    {{ $t('neu_event') }}
                </v-btn>
            </template>

            <!-- Toolbar items - depending on the context, we decide which to show -->
            <template #toolbar-items>
                <!-- standard list context -->
                <template v-if="displayContext === 'list'">
                    |
                    <StatusToggleBtns v-model="statusFilter" :disabled="loading" />

                    <div v-if="userIsStaff()" class="d-flex align-center">
                        |
                        <v-btn-toggle
                            v-if="userIsStaff()"
                            v-model="propFilter"
                            multiple
                            borderless
                            tile
                            background-color="primary"
                        >
                            <!-- editedAfterPublication -->
                            <IconBtn
                                value="btn_changed_after_publication"
                                color="primary"
                                :disabled="loading"
                                icon-cls="editedafterpublication"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('edited_after_publication')"
                            />
                            <!-- toNewsletter -->
                            <IconBtn
                                value="btn_mar"
                                color="primary"
                                :disabled="loading"
                                icon-cls="tonewsletter"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('fuer_nl')"
                            />
                            <!-- tippedEvents -->
                            <IconBtn
                                value="btn_tip"
                                color="primary"
                                :disabled="loading"
                                icon-cls="typed"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('featured')"
                            />
                        </v-btn-toggle>
                    </div>

                    <div v-if="userIsStaff()" class="d-flex align-center">
                        |
                        <v-btn-toggle v-model="bpFilter" borderless tile background-color="primary">
                            <IconBtn
                                value="btn_own"
                                color="primary"
                                :disabled="loading"
                                icon-cls="ownnotimported"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('own_events')"
                            />
                            <!-- fromGuidle -->
                            <IconBtn
                                value="btn_guidle"
                                color="primary"
                                :disabled="loading"
                                icon-cls="guidleimport"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('from_guidle')"
                            />
                            <!-- fromAzizi -->
                            <IconBtn
                                value="btn_azizi"
                                color="primary"
                                :disabled="loading"
                                icon-cls="import"
                                icon-color="white"
                                icon-disabled-color="white"
                                :tooltip="$t('from_azizi')"
                            />
                        </v-btn-toggle>
                    </div>
                    |
                    <!-- showOlder -->
                    <v-btn-toggle v-model="propFilter" multiple borderless tile background-color="primary">
                        <IconBtn
                            value="includeOlder"
                            color="primary"
                            :disabled="loading"
                            icon-cls="allincpast"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('show_older')"
                        />
                    </v-btn-toggle>
                    <v-btn-toggle v-model="selectionFilter" borderless tile background-color="primary">
                        <!-- showChecked -->
                        <IconBtn
                            value="showChecked"
                            color="primary"
                            :disabled="loading"
                            icon-cls="markedevents"
                            icon-color="white"
                            icon-disabled-color="white"
                            :tooltip="$t('show_checked')"
                        />
                    </v-btn-toggle>
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="cancelmarked"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('cancel_checked')"
                        @click="removeSelections"
                    />
                    |
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="download"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('xlsx_export')"
                        @click="xlsxExport"
                    />
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="delete"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('soft_delete')"
                        @click.stop="showSoftDeleteDialog"
                    />
                </template>

                <!-- toolbar items for context "dashboard" -->
                <template v-if="displayContext === 'dashboard'">
                    <v-btn-toggle v-model="dashboardFilter" borderless tile background-color="primary">
                        <!-- future -->
                        <ActionBtn value="next_default">
                            <KpIcon icon-cls="statepublished" color="white" disabled-color="grey" />
                            <span>{{ $t('coming') }}</span>
                        </ActionBtn>
                        <!-- past -->
                        <ActionBtn value="past">
                            <KpIcon icon-cls="allincpast" color="white" disabled-color="grey" />
                            <span>{{ $t('pasts') }}</span>
                        </ActionBtn>
                        <!-- draft -->
                        <ActionBtn value="btn_draft">
                            <KpIcon icon-cls="pencil" color="white" disabled-color="grey" />
                            <span>{{ $t('drafts') }}</span>
                        </ActionBtn>
                        <!-- deleted -->
                        <ActionBtn value="btn_del">
                            <KpIcon icon-cls="trash" color="white" disabled-color="grey" />
                            <span>{{ $t('deleteds') }}</span>
                        </ActionBtn>
                        <!-- all -->
                        <ActionBtn value="btn_all">
                            <KpIcon icon-cls="alignleft" color="white" disabled-color="grey" />
                            <span>{{ $t('alle') }}</span>
                        </ActionBtn>
                    </v-btn-toggle>
                </template>
            </template>

            <template #after-query-field>
                <div
                    :class="{
                        'ml-1': true,
                        'erw-suche-indicator': showEnhancedSearchIndicator,
                    }"
                >
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="advancedsearch"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('erw_suche')"
                        @click="showErwSuche = true"
                    />
                </div>
            </template>

            <template #item.context_menu="{ item }">
                <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                        <v-btn dark icon v-bind="attrs" v-on="on">
                            <KpIcon icon-cls="moredots" color="grey" />
                        </v-btn>
                    </template>

                    <EventContextMenu
                        :record="item"
                        @delete-record="(record) => $refs.eventList.initiateDelete(record)"
                        @reject-record="onRejected"
                        @reload-list="() => $refs.eventList.reload()"
                        @duplicate-record="duplicateEvent($event)"
                    />
                </v-menu>
            </template>
            <!-- <template v-slot:item.quality="{ item }">
                        TODO:
                        <i v-if="Math.random() > 0.0" class="ic ic-12 ic-star ic-col-grey"></i>
                        <i v-if="Math.random() > 0.2" class="ic ic-12 ic-star ic-col-grey"></i>
                        <i v-if="Math.random() > 0.4" class="ic ic-12 ic-star ic-col-grey"></i>
                        <i v-if="Math.random() > 0.6" class="ic ic-12 ic-star ic-col-grey"></i>
                        <i v-if="Math.random() > 0.8" class="ic ic-12 ic-star ic-col-grey"></i>
                    </template> -->
            <template #item.status="{ item }">
                <KpIcon
                    v-if="item.status"
                    :tooltip="$t(item.status)"
                    :icon-cls="`${item.status_icon}`"
                    :color="`${item.status_icon_color}`"
                />
                <KpIcon
                    v-if="item.import_protection"
                    :tooltip="$t('import_protection')"
                    icon-cls="importsafe"
                    color="grey"
                />
            </template>
            <template #item.event_class="{ item }">
                {{ $t(item.event_class || 'event') }}
            </template>
            <template #item.is_featured="{ item }">
                <KpIcon v-if="item.is_featured" :tooltip="$t('featured')" icon-cls="typed" color="ok" />
                <span v-else></span>
            </template>
            <template #item.image="{ item }">
                <v-img :src="getImageUrl(item)" width="100px" height="80px" class="preview-icons">
                    <a v-if="!hidePreview" :href="getDetailPreviewUrl(item)" target="_blank">
                        <KpIcon icon-cls="preview" color="black" :tooltip="$t('prev_detail')" />
                    </a>
                    <a v-if="!hidePreview" :href="getSearchPreviewUrl(item)" target="_blank">
                        <KpIcon icon-cls="list" color="black" :tooltip="$t('prev_list')" />
                    </a>
                </v-img>
            </template>
            <template #item.title="{ item }">
                <list-item-title :item="item" />
            </template>
            <template #item.first_schedule_date="{ item }">
                <span class="date-from"
                    >{{ item.first_schedule_date.isValid() ? item.first_schedule_date.formatIf('dd, DD.MM.YYYY') : '' }}
                </span>
                <span
                    v-if="
                        item.last_schedule_date.isValid() && !item.last_schedule_date.isSame(item.first_schedule_date)
                    "
                    class="date-to"
                    >{{ $t('bis') }} <br />
                    {{ item.last_schedule_date.formatIf('dd, DD.MM.YYYY') }}
                </span>
            </template>
            <template #item.next_schedule_date="{ item }">
                {{ item.next_schedule_date.isValid() ? item.next_schedule_date.formatIf('dd, DD.MM.YYYY') : '' }}
            </template>
            <template #item.created="{ item }">
                <span class="date-from">
                    {{ item.created.isValid() ? item.created.formatIf('dd, DD.MM.YYYY') : '' }}
                </span>
            </template>
            <template #item.bp_import_origin="{ item }">
                <KpIcon
                    v-if="item.bp_import_origin"
                    :tooltip="lookupDataProvider(item.bp_import_origin)?.provider || $t(item.bp_import_origin)"
                    :icon-cls="getImportOriginIconClass(item)"
                />
                <KpIcon v-else :tooltip="$t('own')" icon-cls="ownnotimported" color="primary" />
            </template>
            <template #item.to_newsletter="{ item }">
                <KpIcon v-if="item.to_newsletter" icon-cls="tonewsletter" color="grey" />
            </template>
            <template #item.target_audience="{ item }">
                <span v-if="item.target_audience">
                    <KpIcon icon-cls="tick" color="ok" />
                    {{ item.target_audience }}
                </span>
                <span v-else>
                    <KpIcon icon-cls="x" color="grey" />
                </span>
            </template>
            <template #item.super_sub_event="{ item }">
                <div v-if="item.child_event_ids?.length">
                    <div><strong>Haupt-Eintrag von:</strong></div>
                    <router-link
                        v-for="id in item.child_event_ids.filter((val, idx, self) => self.indexOf(val) === idx)"
                        :key="id"
                        :to="{ name: 'event-edit', params: { id } }"
                        >{{ id }}<br />
                    </router-link>
                </div>
                <div v-if="item.parent_event_ids?.length">
                    <div><strong>Unter-Eintrag von:</strong></div>
                    <router-link
                        v-for="id in item.parent_event_ids.filter((val, idx, self) => self.indexOf(val) === idx)"
                        :key="id"
                        :to="{ name: 'event-edit', params: { id } }"
                        >{{ id }}<br />
                    </router-link>
                </div>
            </template>
        </BaseRemoteList>
        <v-dialog v-model="isSoftDeleteDialogVisible" scrollable persistent max-width="500px">
            <v-card>
                <v-card-title class="text-h5">{{ $t('delete_selected_dialog_title') }}</v-card-title>
                <v-card-text style="max-height: 300px">
                    <v-row v-for="item in allSelectedItems" :key="item.id">
                        <v-col>{{ item.id }}</v-col>
                        <v-col>{{ item.title }}</v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="hideSoftDeleteDialog"> {{ $t('disagree') }} </v-btn>
                    <v-btn color="secondary" text @click="agreeDeletion"> {{ $t('agree') }} </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <PromptDialog
            v-if="showRejectConfirmDlg"
            v-model="showRejectConfirmDlg"
            :title="$t('reject')"
            :message="$t('reject_event_sure')"
            :response-required="false"
            :confirm-label="$t('reject').toLowerCase()"
            :confirm-alternate-label="$t('reject_with_mail')"
            @confirm="doReject"
            @confirm-alternate="doRejectWithMail"
        />
    </v-card>
</template>

<script>
import { Event, EventCollection } from '@/models/Event';
import sessionStorageMixin from '@/mixins/session-storage';
import { globalParam, globalVar } from '@/helpers/globals';
import ListItemTitle from '@/components/event/list-item-title.vue';
import EventEnhancedSearch from '@/components/event/EnhancedSearch.vue';
import EventContextMenu from '@/components/event/ContextMenu.vue';
import StatusToggleBtns from './StatusToggleBtns.vue';
import { mapActions } from 'vuex';
import { userIsStaff } from '@/helpers/auth';
import { errToStr } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';
import KpIcon from '../KpIcon.vue';
import { lookupDataProvider } from '@/helpers/data_provider_composable';
import { getRouteParams } from '@/helpers/eventsOpenMode';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventList',

    components: {
        ListItemTitle,
        EventEnhancedSearch,
        EventContextMenu,
        StatusToggleBtns,
        KpIcon,
    },
    mixins: [sessionStorageMixin],
    props: {
        sessionKey: {
            type: String,
            required: true,
        },
        height: {
            type: [String, Number],
            default: null,
        },
        /**
         * used to tweak several display properties.
         * Allowed values:
         * - list: default, used in generic / full list context
         * - dashboard: if the list is used in the dashboard, a limited set of options is shown
         */
        displayContext: {
            type: String,
            default: 'list',
        },
    },

    data() {
        let searchFilters = {
            selected: this.getSessionValue('selected') || [], // global selected records
            statusFilter: this.getSessionValue('statusFilter') || null,
            dashboardFilter: this.getSessionValue('dashboardFilter') || 'next_default',
            propFilter: this.getSessionValue('propFilter') || [],
            bpFilter: this.getSessionValue('bpFilter') || null,
            guidleFilter: this.getSessionValue('guidleFilter') || null,
            selectionFilter: this.getSessionValue('selectionFilter') || null,

            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},

            // will be set when extended search btn is clicked --> initially taken from extended SearchParams
            enhancedFilters: this.getSessionValue('extendedSearchParams') || {},
        };

        if (this.$route.params?.filters) {
            searchFilters = Object.assign(
                searchFilters,
                {
                    selected: [],
                    statusFilter: null,
                    dashboardFilter: null,
                    propFilter: [],
                    bpFilter: null,
                    guidleFilter: null,
                    selectionFilter: null,
                    extendedSearchParams: {},
                    enhancedFilters: {},
                },
                this.$route.params.filters,
            );
            this.setSessionValue('selected', searchFilters.selected);
            this.setSessionValue('statusFilter', searchFilters.statusFilter);
            this.setSessionValue('dashboardFilter', searchFilters.dashboardFilter);
            this.setSessionValue('propFilter', searchFilters.propFilter);
            this.setSessionValue('bpFilter', searchFilters.bpFilter);
            this.setSessionValue('guidleFilter', searchFilters.guidleFilter);
            this.setSessionValue('extendedSearchParams', searchFilters.extendedSearchParams);
            this.setSessionValue('selectionFilter', searchFilters.selectionFilter);
        }

        let statusFilter = this.$route.params?.status || null;
        if (statusFilter) {
            searchFilters.statusFilter = statusFilter;
        }

        const data = Object.assign(
            {
                cacheBuster: new Date().getTime(),
                loading: false,
                EventCollection,
                // columns differ by role: staff members have a different view:
                headers: userIsStaff()
                    ? // staff column setting:
                      [
                          { width: 35, sortable: false, value: 'context_menu' },
                          // { text: this.$t('quality'), width: 84, sortable: true, value: 'quality' },
                          { text: this.$t('status'), width: 70, sortable: true, value: 'status' },
                          { text: this.$t('quelle'), width: 80, sortable: true, value: 'bp_import_origin' },
                          { text: this.$t('featured'), width: 80, sortable: true, value: 'is_featured' },
                          { text: this.$t('to_newsletter'), width: 105, sortable: true, value: 'to_newsletter' },
                          { text: this.$t('super_sub_event'), width: 105, sortable: false, value: 'super_sub_event' },
                          { width: 100, sortable: false, value: 'image' },
                          { text: this.$t('titel'), width: 340, sortable: true, value: 'title' },
                          { text: this.$t('date'), width: 120, sortable: true, value: 'first_schedule_date' },
                          { text: this.$t('next_date'), width: 130, sortable: true, value: 'next_schedule_date' },
                          { text: this.$t('event_class'), width: 120, sortable: true, value: 'event_class' },
                          { text: this.$t('event_organizer'), width: 200, sortable: true, value: '$.event_organizer' },
                          { text: this.$t('event_location'), width: 250, sortable: true, value: '$.location_title' },
                          { text: this.$t('target_audience_s'), width: 180, sortable: true, value: 'target_audience' },
                          { text: this.$t('termin_tags'), width: 180, sortable: true, value: '$.schedule_tags' },
                          { text: this.$t('sparten_tags'), width: 180, sortable: true, value: '$.tag_names' },
                          { text: this.$t('rubriken'), width: 90, sortable: true, value: '$.event_genre_groups' },
                          { text: this.$t('created'), width: 120, sortable: true, value: 'created' },
                      ]
                    : [
                          // non-staff column setting ("Veranstalter")
                          { width: 35, sortable: false, value: 'context_menu' },
                          // { text: this.$t('quality'), width: 84, sortable: true, value: 'quality' },
                          { text: this.$t('status'), width: 70, sortable: true, value: 'status' },
                          { text: this.$t('quelle'), width: 80, sortable: true, value: 'bp_import_origin' },
                          { text: this.$t('super_sub_event'), width: 105, sortable: false, value: 'super_sub_event' },
                          { width: 100, sortable: false, value: 'image' },
                          { text: this.$t('titel'), width: 340, sortable: true, value: 'title' },
                          { text: this.$t('date'), width: 120, sortable: true, value: 'first_schedule_date' },
                          { text: this.$t('next_date'), width: 130, sortable: true, value: 'next_schedule_date' },
                          { text: this.$t('event_class'), width: 120, sortable: true, value: 'event_class' },
                          { text: this.$t('rubriken'), width: 80, sortable: true, value: '$.event_genre_groups' },
                          { text: this.$t('termin_tags'), width: 180, sortable: true, value: '$.schedule_tags' },
                          { text: this.$t('sparten_tags'), width: 180, sortable: true, value: '$.tag_names' },
                          { text: this.$t('event_location'), width: 250, sortable: true, value: '$.location_title' },
                          { text: this.$t('event_organizer'), width: 200, sortable: true, value: '$.event_organizer' },
                      ],
                selectedItems: [], // selected records on current page
                allSelectedItems: [],
                showErwSuche: false,
                isSoftDeleteDialogVisible: false,
                showRejectConfirmDlg: false,
                recordToReject: null,
            },
            searchFilters,
        );

        return data;
    },

    computed: {
        filters() {
            const filters = [];

            if (this.selectionFilter && this.selected.length > 0) {
                filters.push({
                    property: 'id',
                    value: [...(this.selected.length ? this.selected : [-1])],
                    operator: 'IN',
                });
            }

            return filters;
        },

        extraParams() {
            const keys = [];

            if (this.statusFilter) {
                // special case for filter param: 'status': if the route provides a 'status' param, we
                // set the statusFilter accordingly:
                // remap status: the backend is a bit weird, it asks for "btns" to be pressed. This is an uglyness from the past...:
                let statusFilter =
                    {
                        registered: 'btn_reg',
                        draft: 'btn_draft',
                        imported: 'btn_imported',
                        deleted: 'btn_del',
                        rejected: 'btn_rej',
                        released: 'btn_rel',
                    }[this.statusFilter] || null;
                if (statusFilter) {
                    keys.push(statusFilter);
                }
            }
            for (const prop of this.propFilter) {
                keys.push(prop);
            }
            if (this.bpFilter) {
                keys.push(this.bpFilter);
            }
            if (this.guidleFilter) {
                keys.push(this.guidleFilter);
            }

            const extraParams = {};
            for (const key of keys) {
                extraParams[key] = true;
            }

            const enhancedFilters = {};

            if (!this.selectionFilter) {
                for (const key in this.enhancedFilters) {
                    // the value can be true, false, null, ...
                    if (this.enhancedFilters[key] !== undefined) {
                        enhancedFilters[key] = this.enhancedFilters[key];
                    }
                }
                extraParams.enhancedFilters = enhancedFilters;
            }

            // apply dashboard filter(s):
            this.applyDashboardFilters(extraParams);
            return extraParams;
        },

        showEnhancedSearchIndicator() {
            let show = false;
            for (const key in this.enhancedFilters || {}) {
                if (!Array.isArray(this.enhancedFilters[key]) || this.enhancedFilters[key].length > 0) {
                    show = true;
                }
            }
            return show;
        },

        hidePreview() {
            return globalParam('event.hide_preview');
        },
    },

    watch: {
        statusFilter(value) {
            this.setSessionValue('statusFilter', value || null);
        },
        dashboardFilter(value) {
            this.setSessionValue('dashboardFilter', value || null);
        },
        propFilter(value) {
            this.setSessionValue('propFilter', value || []);
        },
        bpFilter(value) {
            this.setSessionValue('bpFilter', value || null);
        },
        guidleFilter(value) {
            this.setSessionValue('guidleFilter', value || null);
        },
        selectionFilter(value) {
            this.setSessionValue('selectionFilter', value || null);
        },
    },

    created() {
        this.Event = Event;
    },

    methods: {
        ...mapActions(['runAsync']),
        userIsStaff,
        lookupDataProvider,

        onEdit(event) {
            this.openEntry(event);
        },

        onRowDblClick(e, data) {
            if (data.item && data.item.status !== Event.STATUS_DELETED) {
                this.openEntry(data.item);
            }
        },

        onItemSelected({ item, value }) {
            const newIds = [...this.selected];
            if (value === true) {
                newIds.push(item.id);
            } else {
                delete newIds[newIds.indexOf(item.id)];
            }

            // unique and filter nulls
            this.selected = [...new Set(newIds.filter(Boolean))];
            this.setSessionValue('selected', this.selected);
        },

        onLoaded(collection) {
            // re-select the selected items from the session id array:
            if (this.selected.length) {
                this.selectedItems = collection.models.filter((item) => this.selected.indexOf(item.id) > -1);
            } else {
                this.selectedItems = [];
            }
        },

        removeSelections() {
            this.selected = [];
            this.selectionFilter = null;
            this.setSessionValue('selected', this.selected);
        },

        openEntry(event) {
            const { name, id } = getRouteParams(event);

            // On existing records, we route to the info-category panel directly:
            this.$router.push({
                name,
                params: { id, backRoute: { route: this.$route } },
            });
        },

        onNew() {
            this.$router.push({ name: 'event-edit', params: { id: 'new' } });
        },

        onEnhancedSearch(searchParams) {
            searchParams = Object.fromEntries(
                Object.entries(searchParams).filter(([_, v]) => v !== null && v !== undefined && v !== ''),
            );
            this.extendedSearchParams = searchParams;
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });

            this.enhancedFilters = { ...this.extendedSearchParams };
        },

        getImageUrl(record) {
            const conf = globalVar('featured_images_config').event.thumb;
            return `${globalVar('imageroot')}/event/${record.id}/${conf.ident}.${conf.format}?ts=${this.cacheBuster}`;
        },

        getDetailPreviewUrl(record) {
            return record.detailPreviewUrl;
        },

        getSearchPreviewUrl(record) {
            return record.searchPreviewUrl;
        },

        getImportOriginIconClass(record) {
            // let icon = record.bp_import_origin;
            let icon = 'import';
            switch (record.bp_import_origin) {
                case 'guidle':
                    icon = 'guidleimport';
                    break;
            }
            return icon ? `${icon} ic-col-grey` : '';
        },

        applyDashboardFilters(extraParams) {
            if (extraParams && typeof this.dashboardFilter === 'string') {
                extraParams[this.dashboardFilter] = true;
            }
        },

        async duplicateEvent(event) {
            this.loading = true;
            try {
                await api().post('/event/duplicate', {
                    id: event.id,
                });
                this.$refs.eventList.reload();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },

        xlsxExport() {
            this.$refs.eventList.xlsxExport();
        },

        async loadAllSelectedItems() {
            const newColl = new EventCollection();
            const res = await newColl.query({ id: this.selected });
            this.allSelectedItems = res.models;
        },

        async showSoftDeleteDialog() {
            await this.loadAllSelectedItems();
            if (!this.allSelectedItems.length) {
                this.allSelectedItems = [];
            } else {
                this.isSoftDeleteDialogVisible = true;
            }
        },

        hideSoftDeleteDialog() {
            this.isSoftDeleteDialogVisible = false;
        },

        agreeDeletion() {
            this.$refs.eventList.deleteItems(this.allSelectedItems);
            this.removeSelections();
            this.hideSoftDeleteDialog();
        },

        onRejected(record) {
            this.recordToReject = record;
            this.showRejectConfirmDlg = true;
        },

        // duplication from PublishPanel.vue
        async doReject() {
            await this.setStatus(Event.STATUS_REJECTED, false);
            commitToast({ message: this.$t('event_rejected'), color: 'success' });
        },

        // duplication from PublishPanel.vue
        async doRejectWithMail(message) {
            await this.setStatus(Event.STATUS_REJECTED, true, message);
            commitToast({ message: this.$t('event_rejected_with_mail'), color: 'success' });
        },

        // duplication from PublishPanel.vue with a small change
        async setStatus(newStatus, sendMail = true, message = null) {
            try {
                this.$emit('saving', true);
                await api().post('/Event/status', {
                    event_id: this.recordToReject.id,
                    status: newStatus,
                    sendMail,
                    message,
                    edited: false,
                });
                this.$refs.eventList.reload();
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.event-list {
    ::v-deep table > tbody > tr > td {
        vertical-align: top;
        color: var(--gray-text-color);
    }
}
.entry-autor {
    font-style: italic;
}
.date-from {
    color: var(--nearblack-color);
}

.preview-icons {
    ::v-deep {
        .v-responsive__content {
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            a {
                width: 22px;
                height: 22px;
                display: inline-block;
                background-color: var(--tgk-grey-5);
                opacity: 0.4;
                .ic {
                    opacity: 0.4;
                }

                &:hover {
                    opacity: 1;
                    .ic {
                        opacity: 1;
                    }
                }
            }
        }
    }
}
</style>
